// src/platform/app-lifecycle.ts
// App lifecycle events: background/foreground, back button, deep links

import { isNative } from './platform';

type Cleanup = () => void;

/** Listen for app going to background / returning to foreground. */
export function onAppStateChange(callback: (isActive: boolean) => void): Cleanup {
  if (isNative()) {
    let cleanup: Cleanup = () => {};
    import('@capacitor/app').then(({ App }) => {
      const listener = App.addListener('appStateChange', ({ isActive }) => {
        callback(isActive);
      });
      cleanup = () => { listener.then(h => h.remove()); };
    });
    return () => cleanup();
  }

  // Web fallback: visibilitychange
  const handler = () => callback(document.visibilityState === 'visible');
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
}

/** Listen for Android back button. */
export function onBackButton(callback: (canGoBack: boolean) => void): Cleanup {
  if (!isNative()) return () => {};
  let cleanup: Cleanup = () => {};
  import('@capacitor/app').then(({ App }) => {
    const listener = App.addListener('backButton', ({ canGoBack }) => {
      callback(canGoBack);
    });
    cleanup = () => { listener.then(h => h.remove()); };
  });
  return () => cleanup();
}

/** Listen for deep link opens. */
export function onDeepLink(callback: (url: string) => void): Cleanup {
  if (!isNative()) return () => {};
  let cleanup: Cleanup = () => {};
  import('@capacitor/app').then(({ App }) => {
    const listener = App.addListener('appUrlOpen', ({ url }) => {
      callback(url);
    });
    cleanup = () => { listener.then(h => h.remove()); };
  });
  return () => cleanup();
}
